/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
	FC, createContext, useContext, useMemo, useState,
} from 'react';
import { productCategories } from 'utils/product.utils';
import moment from 'moment';
import { IDevice } from '../../entities/device';
import { IReportDate } from '../../entities/report';
import { DeviceApiImpl } from '../../data/device-api-impl';
import useFeedback from 'common/presentation/providers/feedback.provider';
import { ApiResponse } from 'common/data/entities/api-response';
import { useTranslation } from 'react-i18next';
import useEffectCustom from 'common/presentation/hooks/useEffectCustom';
import useAuth from 'features/client-login/presentation/provider/auth.provider';

interface DeviceContextProps {
   loading: boolean;
   getUserProducts: () => void;
   error: string,
   hub?: IDevice[];
   wallBox?: IDevice[];
   analyzer?: IDevice[];
   handleClickShowReport: (ids: number[], dates: IReportDate) => Promise<Blob>
   reportLoading: boolean;
   productTags: string[];
   getProductTags: () => void;
   selectedTag: string;
   setSelectedTag: (tag: string) => void;
}

const DeviceContext = createContext({} as DeviceContextProps);

export const DeviceProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation('translations');

	const { user } = useAuth();

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [allProducts, setAllProducts] = useState<IDevice[]>([] as IDevice[]);
	const [hub, setHub] = useState<IDevice[]>([] as IDevice[]);
	const [wallBox, setWallBox] = useState<IDevice[]>([] as IDevice[]);
	const [analyzer, setAnalyzer] = useState<IDevice[]>([] as IDevice[]);
	const [reportLoading, setReportLoading] = useState(false);
	const [productTags, setProductTags] = useState<string[]>([]);
	const [selectedTag, setSelectedTag] = useState(t('all'));

	const { addToast } = useFeedback();

	const deviceApi = new DeviceApiImpl();

	const filterProductsByCategory = (products: IDevice[] | []) => {
		setHub(products.filter(
			(device) => device.product.code === productCategories.HUB,
		));
		setWallBox(products.filter(
			(device) => (device.product.code === productCategories.WALLBOX_MONOPHASE
			|| device.product.code === productCategories.WALLBOX_THREE_PHASE),
		));
		setAnalyzer(products.filter(
			(device) => (device.product.code === productCategories.ANALYZER_MONOPHASE
				|| device.product.code === productCategories.ANALYZER_THREE_PASE
				|| device.product.code === productCategories.ANALYZER_MONOPHASE_V2
				|| device.product.code === productCategories.ANALYZER_THREE_PASE_V2),
		));
	};

	const getUserProducts = async () => {
		setLoading(true);
		try {
			const deviceResult = await deviceApi.getUserProducts();
			setAllProducts(deviceResult);
			filterProductsByCategory(deviceResult || []);
		} catch (mError) {
			setError((mError as Error).message);
		} finally {
			setLoading(false);
		}
	};

	const handleClickShowReport = async (ids: number[], dates: IReportDate) => {
		setReportLoading(true);
		try {
			const requestParams = {
				userProdIds: ids,
				begin: {
					year: parseInt(moment(dates.startDate).format('YYYY'), 10),
					month: parseInt(moment(dates.startDate).format('MM'), 10),
					day: parseInt(moment(dates.startDate).format('DD'), 10),
				},
				end: {
					year: parseInt(moment(dates.endDate).format('YYYY'), 10),
					month: parseInt(moment(dates.endDate).format('MM'), 10),
					day: parseInt(moment(dates.endDate).format('DD'), 10),
				},
			};
			const deviceResult = await deviceApi.exportWallboxExcel(requestParams);
			return deviceResult;
		} catch (mError: any) {
			const errorJson = await mError?.response?.data.text();
			const errorResponse = JSON.parse(errorJson) as ApiResponse<void>;
			addToast({ message: errorResponse.message, title: errorResponse.title, error: true });
			throw mError;
		} finally {
			setReportLoading(false);
		}
	};

	const getProductTags = async () => {
		try {
			const tagsResult = await deviceApi.getUserProductTags();
			setProductTags([t('all'), t('my_devices'), ...tagsResult.data]);
		} catch (mError: any) {
			const errorJson = await mError?.response?.data.text();
			const errorResponse = JSON.parse(errorJson) as ApiResponse<void>;
			addToast({ message: errorResponse.message, title: errorResponse.title, error: true });
		}
	};

	const filterProductsByTag = () => {
		if (selectedTag === t('all')) {
			filterProductsByCategory(allProducts);
		} else if (selectedTag === t('my_devices')) {
			filterProductsByCategory(allProducts.filter((device) => device.userID === user?.userID));
		} else {
			filterProductsByCategory(allProducts.filter((device) => device.tags.some((tag) => tag.name === selectedTag)));
		}
	};

	useEffectCustom(() => {
		filterProductsByTag();
	}, [selectedTag]);

	const value = useMemo(() => ({
		loading,
		getUserProducts,
		error,
		hub,
		wallBox,
		analyzer,
		handleClickShowReport,
		reportLoading,
		getProductTags,
		productTags,
		selectedTag,
		setSelectedTag,
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [
		loading,
		hub,
		wallBox,
		analyzer,
		reportLoading,
		productTags,
		selectedTag,
		error]);
	return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export default function useDevices() {
	return useContext(DeviceContext);
}
